// ============================================================================
// UTILITY METHODS - Ratio Calculation
// ============================================================================

/**
 * Converts decimal ratio or width/height to a readable format
 * Calculates GCD to simplify ratios (e.g., 1920:1080 -> 16:9)
 * Also computes missing dimensions when possible
 * 
 * @param specs - Object containing width, height, and/or ratio
 * @returns Formatted string like "16:9" or "1920x1080px, aspect ratio 16:9"
 * @throws Error if neither ratio nor both dimensions are provided
 */
export function decimalToImageRatio(specs: {
    width?: number;
    height?: number;
    ratio?: number
}): string {
    // Validate input - need either ratio or both dimensions
    if (!specs.ratio && (!specs.width || !specs.height)) {
        throw new Error('Provide either ratio, or width and height.');
    }

    /**
     * Greatest Common Divisor calculation using Euclidean algorithm
     * Used to simplify ratios to their simplest form
     */
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

    let finalNumerator: number = 0;
    let finalDenominator: number = 0;

    // CASE 1: Both width and height provided
    if (specs.width && specs.height) {
        const divisor = gcd(specs.width, specs.height);
        finalNumerator = specs.width / divisor;
        finalDenominator = specs.height / divisor;
    }
    // CASE 2: Only ratio provided
    else if (specs.ratio) {
        // Convert decimal ratio to fraction with precision
        const numerator = Math.round(specs.ratio * 10000);
        const denominator = 10000;
        const divisor = gcd(numerator, denominator);
        finalNumerator = numerator / divisor;
        finalDenominator = denominator / divisor;

        // Calculate missing dimension if one is provided
        if (specs.width && !specs.height) {
            specs.height = Math.round((specs.width / finalNumerator) * finalDenominator);
        }
        if (specs.height && !specs.width) {
            specs.width = Math.round((specs.height / finalDenominator) * finalNumerator);
        }
    }

    // Format output based on available information
    if (specs.width && specs.height) {
        return `Recommended: ${Math.round(specs.width)}x${Math.round(specs.height)}px , aspect ratio ${finalNumerator}:${finalDenominator}`;
    }

    return `${finalNumerator}:${finalDenominator}`;
}

/**
 * Formats file size in bytes to a human-readable string (e.g., "1.5 MB")
 * @param bytes - Size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export interface UploadMetaData {
    uploadExtType: 'image' | 'video' | 'media' | 'file';
    uploadExtensionsByType: string[];
    uploadTypeLabel: string;
    visibleExts: string[];
    hiddenExts: string[];
    hasHidden: boolean;
}